/**
 * 🛠️ Asset Utilities
 *
 * Asset CodeGen에 의해 자동 생성된 유틸리티 함수들
 */

import React from 'react';
import { AssetInfo, AssetName, AssetSize, AssetColor, assetPathMap, sizeMap, colorMap } from './types';

// assetDir별 basePath 매핑 생성
const assetDirBasePathMap = new Map<string, string>();
assetDirBasePathMap.set('auth', '/3.0');
assetDirBasePathMap.set('icon', '/3.0');
assetDirBasePathMap.set('payment', '/3.0');
assetDirBasePathMap.set('shop', '/3.0');
assetDirBasePathMap.set('tournament', '/3.0');

/**
 * Asset 경로 가져오기
 */
export function getAssetPath(name: AssetName): string {
	const assetInfo = assetPathMap[name];
	if (!assetInfo) {
		console.warn(`Asset "${name}" not found in assetPathMap`);
		return '';
	}
	const basePath = assetDirBasePathMap.get(assetInfo.assetDir) || '';
	return `${basePath}/${assetInfo.assetDir}/${assetInfo.path}`;
}

/**
 * Asset 정보 가져오기
 */
export function getAssetInfo(name: AssetName): AssetInfo | null {
	return assetPathMap[name] || null;
}

/**
 * 사이즈 스타일을 계산하는 유틸리티 함수
 */
export function getSizeStyle(size?: AssetSize, ratio?: number, style?: React.CSSProperties): React.CSSProperties {
	// size prop이 명시적으로 없거나 undefined인 경우
	if (size === undefined) {
		// ratio가 1이고 CSS에서 width나 height가 지정된 경우, 정사각형으로 만들기
		if (ratio === 1) {
			if (style?.width) {
				return { height: style.width };
			}
			if (style?.height) {
				return { width: style.height };
			}
		}
		return {};
	}

	// 숫자나 문자열이 들어온 경우 -> 객체로 변환
	let normalizedSize: { width?: number; height?: number } | undefined;

	if (typeof size === 'number') {
		// 숫자 -> { width: number, height: number } 객체로 변환
		normalizedSize = { width: size, height: size };
	} else if (typeof size === 'string' && size in sizeMap) {
		// 문자열 사이즈 -> { width: number, height: number } 객체로 변환
		const numSize = sizeMap[size as keyof typeof sizeMap];
		normalizedSize = { width: numSize, height: numSize };
	} else if (typeof size === 'object' && size) {
		// 이미 객체인 경우 그대로 사용
		normalizedSize = size;
	} else {
		// 기본값
		const defaultSize = sizeMap.md;
		normalizedSize = { width: defaultSize, height: defaultSize };
	}

	// 객체 처리 로직 (숫자도 이제 여기를 탐)
	if (normalizedSize) {
		if ('width' in normalizedSize && 'height' in normalizedSize) {
			// 둘 다 지정된 경우 - 정확한 크기
			return {
				width: normalizedSize.width,
				height: normalizedSize.height,
			};
		}
		if ('width' in normalizedSize && !('height' in normalizedSize)) {
			// width만 지정 - ratio가 1이면 무조건 정사각형
			if (ratio === 1) {
				return {
					width: normalizedSize.width,
					height: normalizedSize.width,
				};
			}
			return {
				width: normalizedSize.width,
				height: 'auto',
			};
		}
		if ('height' in normalizedSize && !('width' in normalizedSize)) {
			// height만 지정 - ratio가 1이면 무조건 정사각형
			if (ratio === 1) {
				return {
					width: normalizedSize.height,
					height: normalizedSize.height,
				};
			}
			return {
				width: 'auto',
				height: normalizedSize.height,
			};
		}
	}

	// fallback (여기 도달하면 안 됨)
	return {
		width: sizeMap.md,
		height: sizeMap.md,
	};
}

/**
 * 색상 값을 반환하는 유틸리티 함수
 */
export function getAssetColor(color?: AssetColor): string | undefined {
	if (!color) return undefined;
	if (typeof color === 'string' && color in colorMap) {
		return colorMap[color as keyof typeof colorMap];
	}
	return color;
}

/**
 * 최종 스타일을 조합하는 유틸리티 함수
 */
export function createCommonStyle(
	sizeStyle: React.CSSProperties,
	color: string | undefined,
	style?: React.CSSProperties
): React.CSSProperties {
	return {
		...sizeStyle,
		color,
		...style,
	};
}

/**
 * 에러/미발견 UI 엘리먼트를 생성하는 유틸리티 함수
 */
export function createErrorElement(
	type: 'error' | 'not-found',
	sizeStyle: React.CSSProperties,
	className: string = '',
	style?: React.CSSProperties,
	name?: string
): React.ReactElement {
	const isError = type === 'error';

	return React.createElement(
		'div',
		{
			className: `asset-${type} ${className}`,
			style: {
				...sizeStyle,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				background: '#f8f9fa',
				border: isError ? '1px solid #e9ecef' : '1px dashed #dee2e6',
				borderRadius: '4px',
				fontSize: '12px',
				color: '#6c757d',
				...style,
			},
		},
		isError ? '❌' : `❓ ${name || ''}`
	);
}

/**
 * Asset 존재 여부 확인
 */
export function hasAsset(name: string): name is AssetName {
	return name in assetPathMap;
}

/**
 * 모든 Asset 이름 가져오기
 */
export function getAllAssetNames(): AssetName[] {
	return Object.keys(assetPathMap) as AssetName[];
}
